import React, { useEffect, useState, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, ClipboardList } from 'lucide-react';
import { getAllNcrs, deleteNcr, type Ncr } from '../../services/ncrService.ts';
import './NcrList.css';

const NcrList: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [ncrs, setNcrs] = useState<Ncr[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchNcrs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllNcrs();
      setNcrs(data);
    } catch (error: any) {
      notify(error.message || 'Failed to fetch NCRs', { type: 'warning' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchNcrs();
  }, [fetchNcrs]);

  const handleDelete = async (id: number | undefined) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this NCR?')) return;
    try {
      await deleteNcr(id);
      notify('NCR deleted successfully', { type: 'info' });
      setNcrs(ncrs.filter((n) => n.id !== id));
    } catch (error: any) {
      notify(error.message || 'Failed to delete NCR', { type: 'warning' });
    }
  };

  const filteredNcrs = ncrs.filter((ncr) => {
    const s = searchTerm.toLowerCase();
    return (
      ncr.ncrNumber.toLowerCase().includes(s) ||
      (ncr.description && ncr.description.toLowerCase().includes(s)) ||
      (ncr.templateName && ncr.templateName.toLowerCase().includes(s))
    );
  });

  const normalizeStatus = (status?: string) => {
    const v = (status || '').toLowerCase();
    if (v === 'in-progress' || v === 'in progress') return 'in-progress';
    if (v === 'close' || v === 'closed' || v === 'completed') return 'close';
    return 'open';
  };

  const getStatusLabel = (status?: string) => {
    const v = normalizeStatus(status);
    if (v === 'in-progress') return 'In Progress';
    if (v === 'close') return 'Completed';
    return 'Open';
  };

  return (
    <div className="nl-container">
      {/* ── Header ── */}
      <div className="nl-header">
        <div className="nl-header-left">
          <p className="nl-eyebrow">Non-Conformance Reports</p>
          <h1 className="nl-title">NCR Management</h1>
        </div>
        <button className="nl-create-btn" onClick={() => navigate('/Ncr/create')}>
          <Plus size={16} />
          Create NCR
        </button>
      </div>

      {/* ── Search bar ── */}
      <div className="nl-toolbar">
        <div className="nl-search">
          <Search size={16} className="nl-search-icon" />
          <input
            type="text"
            placeholder="Search by NCR number, description or template…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="nl-search-clear" onClick={() => setSearchTerm('')}>✕</button>
          )}
        </div>
        <span className="nl-count">
          {filteredNcrs.length} of {ncrs.length} records
        </span>
      </div>

      {/* ── Table area ── */}
      <div className="nl-body">
        {loading ? (
          <div className="nl-state">
            <div className="nl-spinner" />
            <p>Loading NCRs…</p>
          </div>
        ) : filteredNcrs.length === 0 ? (
          <div className="nl-state">
            <ClipboardList size={48} className="nl-empty-icon" />
            <p className="nl-empty-title">
              {searchTerm ? `No results for "${searchTerm}"` : 'No NCRs yet'}
            </p>
            <p className="nl-empty-sub">
              {searchTerm
                ? 'Try a different search term.'
                : 'Create your first NCR to get started.'}
            </p>
            {!searchTerm && (
              <button className="nl-create-btn" onClick={() => navigate('/Ncr/create')}>
                <Plus size={16} /> Create NCR
              </button>
            )}
          </div>
        ) : (
          <table className="nl-table">
            <thead>
              <tr>
                <th>NCR Number</th>
                <th>Template</th>
                <th>Description</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredNcrs.map((ncr) => (
                <tr key={ncr.id} onClick={() => navigate(`/Ncr/${ncr.id}`)} className="nl-row">
                  <td>
                    <span className="nl-ncr-number">{ncr.ncrNumber || '—'}</span>
                  </td>
                  <td className="nl-muted">{ncr.templateName || '—'}</td>
                  <td>
                    <span className="nl-description" title={ncr.description}>
                      {ncr.description || '—'}
                    </span>
                  </td>
                  <td className="nl-muted">{ncr.ownerName || '—'}</td>
                  <td>
                    <span className={`nl-badge nl-badge--${normalizeStatus(ncr.status)}`}>
                      {getStatusLabel(ncr.status)}
                    </span>
                  </td>
                  <td className="nl-muted">
                    {ncr.createdAt ? new Date(ncr.createdAt).toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="nl-actions">
                      <button
                        className="inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md transition-colors"
                        title="View"
                        onClick={() => navigate(`/Ncr/${ncr.id}`)}
                      >
                        View
                      </button>
                      <button
                        className="inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-100 rounded-md transition-colors"
                        title="Edit"
                        onClick={() => navigate(`/Ncr/${ncr.id}/edit`)}
                      >
                        Edit
                      </button>
                      <button
                        className="inline-flex w-full items-center justify-center px-2 py-1 text-xs font-semibold text-red-700 border border-red-200 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                        onClick={() => handleDelete(ncr.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default NcrList;
